import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useRegistrationStore } from '../../store/registrationStore';
import { useSaveDraft, useSubmitRegistration, useMyRegistration } from '../../hooks/useRegistration';
import { useReunion } from '../../contexts/ReunionContext';
import { LoadingSpinner } from '../LoadingSpinner';
import { StepIndicator } from './StepIndicator';
import type { Registration } from '../../types/registration';

// Step components — created in subsequent tasks; lazy-guard with placeholder until they exist
import { StepGroupLeader } from './StepGroupLeader';
import { StepFamilyMembers } from './StepFamilyMembers';
import { StepVillaPreference } from './StepVillaPreference';
import { StepSpecialNeeds } from './StepSpecialNeeds';
import { StepReviewSubmit } from './StepReviewSubmit';

const STEPS = [
  { label: 'Group Leader' },
  { label: 'Family Members' },
  { label: 'Room Accommodations' },
  { label: 'Special Needs' },
  { label: 'Review & Submit' },
];

/** Format an ISO timestamp as a relative time string (e.g. "2 min ago") */
function formatRelativeTime(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Your registration has been submitted and is pending review.',
  confirmed: 'Your registration has been confirmed!',
  waitlisted: 'You are currently on the waitlist. We will notify you if a spot opens up.',
  cancelled: 'Your registration has been cancelled.',
};

function RegistrationStatusCard({ registration }: { registration: Registration }) {
  const statusColor: Record<string, string> = {
    pending: 'bg-info-light border-info-border text-info',
    confirmed: 'bg-success-light border-success-border text-success',
    waitlisted: 'bg-warning-light border-warning-border text-warning',
    cancelled: 'bg-error-light border-error-border text-error',
  };

  return (
    <div className={`max-w-xl mx-auto p-6 rounded-lg border ${statusColor[registration.status] ?? 'border-slate-200'}`}>
      <h2 className="text-lg font-bold mb-2 capitalize">{registration.status}</h2>
      <p>{STATUS_LABELS[registration.status] ?? 'Your registration status is being processed.'}</p>
      {registration.submittedAt && (
        <p className="text-xs mt-3 opacity-70">
          Submitted {formatRelativeTime(registration.submittedAt)}
        </p>
      )}
      {(registration.status === 'confirmed' || registration.status === 'pending') && (
        <Link
          to="/registration/manage"
          className="inline-block mt-4 text-sm font-medium text-primary hover:underline"
        >
          Edit Registration &rarr;
        </Link>
      )}
    </div>
  );
}

export function MultiStepForm() {
  const { currentStep, nextStep, prevStep, lastSavedAt, isSaving } = useRegistrationStore();
  const { data: existingReg, isLoading } = useMyRegistration();
  const { reunion } = useReunion();
  const saveDraft = useSaveDraft();
  const submitRegistration = useSubmitRegistration();

  const buildSavePayload = useCallback(() => {
    const store = useRegistrationStore.getState();
    return {
      isGroupLeader: store.groupLeaderData.isGroupLeader,
      phone: store.groupLeaderData.phone,
      familyConnection: store.groupLeaderData.familyConnection || null,
      familyMembers: store.familyMembers.map((fm, i) => ({
        ...fm,
        sortOrder: i,
        dietaryRestrictions:
          store.specialNeeds.memberNeeds[i]?.dietaryRestrictions ?? fm.dietaryRestrictions ?? [],
        foodAllergies:
          store.specialNeeds.memberNeeds[i]?.foodAllergies || fm.foodAllergies || null,
        accessibilityNeeds:
          store.specialNeeds.memberNeeds[i]?.accessibilityNeeds || fm.accessibilityNeeds || null,
      })),
      villaPreference: {
        villaTypePreference: store.villaPreference.villaTypePreference,
        flexibilityNotes: store.villaPreference.flexibilityNotes,
        roomConfigNotes: store.villaPreference.roomConfigNotes,
      },
    };
  }, []);

  // Auto-save draft on step navigation
  const handleNext = useCallback(() => {
    saveDraft.mutate(buildSavePayload());
    nextStep();
  }, [saveDraft, buildSavePayload, nextStep]);

  const handleBack = useCallback(() => {
    saveDraft.mutate(buildSavePayload());
    prevStep();
  }, [saveDraft, buildSavePayload, prevStep]);

  const handleSubmit = useCallback(async () => {
    const response = await saveDraft.mutateAsync(buildSavePayload());
    const regId = response.registration?.id;
    if (regId) {
      await submitRegistration.mutateAsync(regId);
    }
  }, [saveDraft, submitRegistration, buildSavePayload]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // If registration is closed and user has no existing registration, show banner
  if (!existingReg && reunion) {
    if (!reunion.registrationOpen) {
      return (
        <div className="max-w-xl mx-auto p-6 rounded-lg border border-amber-200 bg-amber-50 text-center">
          <h3 className="text-lg font-bold text-slate-900 mb-2">Registration is Closed</h3>
          <p className="text-slate-600 text-sm">Registration is not currently open. Please check back later.</p>
        </div>
      );
    }
    const now = new Date();
    if (reunion.registrationOpenDate && now < new Date(reunion.registrationOpenDate)) {
      return (
        <div className="max-w-xl mx-auto p-6 rounded-lg border border-amber-200 bg-amber-50 text-center">
          <h3 className="text-lg font-bold text-slate-900 mb-2">Registration Not Yet Open</h3>
          <p className="text-slate-600 text-sm">Registration opens on {new Date(reunion.registrationOpenDate).toLocaleDateString()}.</p>
        </div>
      );
    }
    if (reunion.registrationCloseDate && now > new Date(reunion.registrationCloseDate)) {
      return (
        <div className="max-w-xl mx-auto p-6 rounded-lg border border-amber-200 bg-amber-50 text-center">
          <h3 className="text-lg font-bold text-slate-900 mb-2">Registration Closed</h3>
          <p className="text-slate-600 text-sm">Registration closed on {new Date(reunion.registrationCloseDate).toLocaleDateString()}.</p>
        </div>
      );
    }
  }

  // If registration already submitted, show status message
  if (existingReg && existingReg.status !== 'draft') {
    return <RegistrationStatusCard registration={existingReg} />;
  }

  const stepComponents = [
    <StepGroupLeader key="step-0" onNext={handleNext} />,
    <StepFamilyMembers key="step-1" onNext={handleNext} onBack={handleBack} />,
    <StepVillaPreference key="step-2" onNext={handleNext} onBack={handleBack} />,
    <StepSpecialNeeds key="step-3" onNext={handleNext} onBack={handleBack} />,
    <StepReviewSubmit key="step-4" onBack={handleBack} onSubmit={handleSubmit} />,
  ];

  return (
    <div className="max-w-3xl mx-auto">
      <StepIndicator currentStep={currentStep} steps={STEPS} />

      {/* Draft save indicator */}
      <div className="text-xs text-slate-400 text-right mb-4 h-4">
        {isSaving
          ? 'Saving draft...'
          : lastSavedAt
            ? `Draft saved ${formatRelativeTime(lastSavedAt)}`
            : ''}
      </div>

      {/* Current step */}
      {stepComponents[currentStep]}
    </div>
  );
}
